// src/pages/Home.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import {
    startOfYear, endOfYear,
    format, getYear, getMonth, setMonth, setYear
} from 'date-fns';

import {
    Box, Container, Typography, Grid, Card, CardContent, CardActionArea,
    AppBar, Toolbar, IconButton, Tooltip, Chip, LinearProgress, Avatar, CircularProgress
} from '@mui/material';
import {
    Logout as LogoutIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    AccountBalance as AccountBalanceIcon,
    CalendarMonth as CalendarMonthIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const Home = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [monthlyData, setMonthlyData] = useState({});
    const [loading, setLoading] = useState(true);
    const today = new Date();

    const fetchYearData = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);

        const yearStart = startOfYear(new Date(currentYear, 0, 1));
        const yearEnd = endOfYear(new Date(currentYear, 0, 1));

        const q = query(
            collection(db, 'transactions'),
            where('userId', '==', currentUser.uid),
            where('date', '>=', yearStart),
            where('date', '<=', yearEnd)
        );

        const snapshot = await getDocs(q);
        const byMonth = {};

        snapshot.forEach(doc => {
            const t = doc.data();
            const date = t.date.toDate();
            const monthIdx = getMonth(date);
            if (!byMonth[monthIdx]) byMonth[monthIdx] = { income: 0, expenses: 0 };
            if (t.type === 'income') {
                byMonth[monthIdx].income += t.amount;
            } else {
                byMonth[monthIdx].expenses += t.amount;
            }
        });

        setMonthlyData(byMonth);
        setLoading(false);
    }, [currentUser, currentYear]);

    useEffect(() => {
        fetchYearData();
    }, [fetchYearData]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const goToMonth = (monthIdx) => {
        const monthStr = format(setMonth(setYear(new Date(), currentYear), monthIdx), 'yyyy-MM');
        navigate(`/dashboard/${monthStr}`);
    };

    const annualTotals = Object.values(monthlyData).reduce(
        (acc, m) => ({ income: acc.income + m.income, expenses: acc.expenses + m.expenses }),
        { income: 0, expenses: 0 }
    );
    const annualBalance = annualTotals.income - annualTotals.expenses;

    const maxMonthlyValue = Math.max(
        ...Object.values(monthlyData).map(m => Math.max(m.income, m.expenses)),
        1
    );

    const userInitial = currentUser?.email?.[0]?.toUpperCase() || 'U';

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            {/* AppBar */}
            <AppBar position="sticky">
                <Toolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '8px',
                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <AccountBalanceIcon sx={{ fontSize: 18, color: 'white' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                            Minha Contabilidade
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary', fontSize: '0.875rem' }}>
                            {currentUser?.email}
                        </Typography>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 700 }}>
                            {userInitial}
                        </Avatar>
                        <Tooltip title="Sair">
                            <IconButton color="inherit" onClick={handleLogout} size="small">
                                <LogoutIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Year selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                            Visão Anual
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                            Acompanhe suas finanças mês a mês
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => setCurrentYear(y => y - 1)} size="small"
                            sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={700} sx={{ minWidth: 60, textAlign: 'center' }}>
                            {currentYear}
                        </Typography>
                        <IconButton onClick={() => setCurrentYear(y => y + 1)} size="small"
                            sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Annual Summary Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <AnnualCard
                            title="Total Entradas"
                            value={annualTotals.income}
                            icon={<TrendingUpIcon />}
                            color="#10b981"
                            gradient="linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AnnualCard
                            title="Total Despesas"
                            value={annualTotals.expenses}
                            icon={<TrendingDownIcon />}
                            color="#f43f5e"
                            gradient="linear-gradient(135deg, rgba(244,63,94,0.15), rgba(244,63,94,0.05))"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AnnualCard
                            title="Saldo do Ano"
                            value={annualBalance}
                            icon={<AccountBalanceIcon />}
                            color={annualBalance >= 0 ? '#6366f1' : '#f43f5e'}
                            gradient={annualBalance >= 0
                                ? "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(99,102,241,0.05))"
                                : "linear-gradient(135deg, rgba(244,63,94,0.15), rgba(244,63,94,0.05))"}
                            isBold
                        />
                    </Grid>
                </Grid>

                {/* Month Grid */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                        Meses do Ano
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        Clique em um mês para ver os lançamentos detalhados
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <Grid container spacing={2} className="fade-in">
                        {MONTHS.map((monthName, idx) => {
                            const data = monthlyData[idx] || { income: 0, expenses: 0 };
                            const balance = data.income - data.expenses;
                            const isCurrentMonth = idx === getMonth(today) && currentYear === getYear(today);
                            const isFuture = new Date(currentYear, idx, 1) > today;
                            const hasData = data.income > 0 || data.expenses > 0;
                            const incomeProgress = (data.income / maxMonthlyValue) * 100;
                            const expenseProgress = (data.expenses / maxMonthlyValue) * 100;

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            border: isCurrentMonth ? '1px solid rgba(99,102,241,0.5)' : undefined,
                                            boxShadow: isCurrentMonth ? '0 0 0 1px rgba(99,102,241,0.3), 0 4px 24px rgba(0,0,0,0.3)' : undefined,
                                            opacity: isFuture ? 0.6 : 1,
                                        }}
                                    >
                                        <CardActionArea onClick={() => goToMonth(idx)} sx={{ height: '100%', p: 0 }}>
                                            <CardContent sx={{ p: 2.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight={700}>
                                                            {monthName}
                                                        </Typography>
                                                        {isCurrentMonth && (
                                                            <Chip label="Atual" size="small" color="primary"
                                                                sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }} />
                                                        )}
                                                    </Box>
                                                    <Box sx={{
                                                        width: 36, height: 36, borderRadius: '8px',
                                                        background: isCurrentMonth
                                                            ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                                                            : 'rgba(148,163,184,0.1)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                    }}>
                                                        <CalendarMonthIcon sx={{ fontSize: 18, color: isCurrentMonth ? 'white' : 'text.secondary' }} />
                                                    </Box>
                                                </Box>

                                                {hasData ? (
                                                    <>
                                                        <Typography variant="h6" fontWeight={700}
                                                            color={balance >= 0 ? 'success.main' : 'error.main'}
                                                            sx={{ mb: 1.5 }}>
                                                            {formatCurrency(balance)}
                                                        </Typography>
                                                        <Box sx={{ mb: 0.5 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="caption" color="success.main">↑ {formatCurrency(data.income)}</Typography>
                                                            </Box>
                                                            <LinearProgress variant="determinate" value={incomeProgress}
                                                                sx={{ height: 4, mb: 1, '& .MuiLinearProgress-bar': { backgroundColor: '#10b981' } }} />
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="caption" color="error.main">↓ {formatCurrency(data.expenses)}</Typography>
                                                            </Box>
                                                            <LinearProgress variant="determinate" value={expenseProgress}
                                                                sx={{ height: 4, '& .MuiLinearProgress-bar': { backgroundColor: '#f43f5e' } }} />
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                                                        {isFuture ? 'Mês futuro' : 'Sem lançamentos'}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Container>
        </Box>
    );
};

const AnnualCard = ({ title, value, icon, color, gradient, isBold }) => (
    <Card sx={{ background: gradient, border: `1px solid ${color}22` }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>{title}</Typography>
                    <Typography variant="h5" fontWeight={isBold ? 800 : 700} color={color}>
                        {formatCurrency(value)}
                    </Typography>
                </Box>
                <Box sx={{
                    width: 44, height: 44, borderRadius: '10px',
                    backgroundColor: `${color}22`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: color
                }}>
                    {icon}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default Home;
